// NameMismatchModal — read-only warning shown when a receipt's names don't
// line up (bank account holder vs customer, or receipt customer vs booking).
// Mirrors the legacy covd_receipt.php mismatch modal: a short explanation
// plus a side-by-side "found vs expected" comparison per issue. No score is
// shown (parity with legacy) and nothing here mutates data.

import { AlertTriangle } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal/Modal';
import type { ReceiptNameMismatch } from '../../../types/api';
import styles from './NameMismatchModal.module.css';

export interface NameMismatchModalProps {
  open: boolean;
  onClose: () => void;
  mismatch: ReceiptNameMismatch;
  /** Receipt identifier shown in the intro line (unique_id / reference). */
  receiptRef: string;
}

export function NameMismatchModal({
  open,
  onClose,
  mismatch,
  receiptRef,
}: NameMismatchModalProps): JSX.Element {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={
        <span className={styles.titleRow}>
          <AlertTriangle size={18} aria-hidden />
          Possible Mismatch
        </span>
      }
    >
      <div className={styles.container}>
        <p className={styles.intro}>
          A name discrepancy was detected on receipt <strong>{receiptRef}</strong>.
          This may mean the receipt was submitted for the wrong customer. Please
          review and correct if needed.
        </p>
        {mismatch.issues.map((issue, i) => (
          <div key={i} className={styles.issueCard}>
            <p className={styles.issueLabel}>{issue.label}</p>
            <div className={styles.compareRow}>
              <div className={styles.compareCell}>
                <span className={styles.cellLabel}>{issue.foundLabel}</span>
                <span className={`${styles.cellValue} ${styles.found}`}>
                  {issue.found}
                </span>
              </div>
              <div className={styles.compareCell}>
                <span className={styles.cellLabel}>{issue.expectedLabel}</span>
                <span className={`${styles.cellValue} ${styles.expected}`}>
                  {issue.expected}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
